import React, { useState, useEffect } from "react";
import { Link, useNavigate } from "react-router-dom";
import { apiService } from "../services/api";
import { CompanyStats, CallAnalysis, Salesperson } from "../types/api";
import UsageDisplay from "../components/common/UsageDisplay";
import AddSalespersonModal from "../components/forms/AddSalespersonModal";
import CallsPerformanceChart from "../components/charts/CallsPerformanceChart";
import CallsVolumeChart from "../components/charts/CallsVolumeChart";
import {
  MicrophoneIcon,
  ClockIcon,
  UserPlusIcon,
  MagnifyingGlassIcon,
  UserGroupIcon,
  UsersIcon,
} from "@heroicons/react/24/outline";

const Dashboard: React.FC = () => {
  const [stats, setStats] = useState<CompanyStats | null>(null);
  const [recentCalls, setRecentCalls] = useState<CallAnalysis[]>([]);
  const [loading, setLoading] = useState(true);
  const [showAddModal, setShowAddModal] = useState(false);

  // Employee search state
  const [employeeSearchTerm, setEmployeeSearchTerm] = useState("");
  const [searchResults, setSearchResults] = useState<Salesperson[]>([]);
  const [showSearchResults, setShowSearchResults] = useState(false);
  const [searchLoading, setSearchLoading] = useState(false);
  const [usageStats, setUsageStats] = useState<any>(null);

  const navigate = useNavigate();

  const loadDashboardData = async () => {
    try {
      const [statsResponse, callsResponse, usageResponse] = await Promise.all([
        apiService.getCompanyStats(),
        apiService.getCalls({
          limit: 5,
          sortBy: "created_at",
          sortOrder: "desc",
        }),
        apiService.getCompanyUsageStats(),
      ]);

      if (statsResponse.success) {
        setStats(statsResponse.data!);
      }

      if (callsResponse.success) {
        setRecentCalls(callsResponse.data.analyses);
      }

      if (usageResponse.success) {
        setUsageStats(usageResponse.data);
      }
    } catch (error) {
      console.error("Failed to load dashboard data:", error);
    } finally {
      setLoading(false);
    }
  };

  // Employee search functionality
  // Dashboard.tsx - Employee search functionality
  const searchEmployees = async (searchTerm: string) => {
    if (!searchTerm.trim()) {
      setSearchResults([]);
      setShowSearchResults(false);
      return;
    }

    setSearchLoading(true);
    try {
      const response = await apiService.searchSalespeople({
        q: searchTerm,
        limit: 10,
      });

      if (response.success && response.data) {
        // FIXED: Use the correct property name
        setSearchResults(response.data.salespeople || []);
        setShowSearchResults(true);
      }
    } catch (error) {
      console.error("Failed to search employees:", error);
      setSearchResults([]);
    } finally {
      setSearchLoading(false);
    }
  };

  const handleEmployeeSearchChange = (value: string) => {
    setEmployeeSearchTerm(value);

    // Debounce search
    const timeoutId = setTimeout(() => {
      searchEmployees(value);
    }, 300);

    return () => clearTimeout(timeoutId);
  };

  const selectEmployee = (employee: Salesperson) => {
    setShowSearchResults(false);
    setEmployeeSearchTerm("");
    navigate(`/salesperson/${employee.id}`);
  };

  useEffect(() => {
    loadDashboardData();
  }, []);

  // Close search results when clicking outside
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      const searchContainer = document.getElementById(
        "employee-search-container",
      );
      if (searchContainer && !searchContainer.contains(event.target as Node)) {
        setShowSearchResults(false);
      }
    };

    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  const getStatusColor = (status: string) => {
    switch (status) {
      case "completed":
        return "text-green-600 bg-green-100";
      case "processing":
      case "transcribing":
      case "analyzing":
        return "text-yellow-600 bg-yellow-100";
      case "failed":
        return "text-red-600 bg-red-100";
      default:
        return "text-gray-600 bg-gray-100";
    }
  };

  const getGradeColor = (grade?: number) => {
    if (!grade) return "text-gray-600";
    if (grade >= 8) return "text-green-600";
    if (grade >= 6) return "text-yellow-600";
    return "text-red-600";
  };

  const formatGrade = (grade?: number) => {
    if (!grade || isNaN(grade)) return "N/A";
    return parseFloat(grade.toString()).toFixed(1);
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600"></div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="md:flex md:items-center md:justify-between">
        <div className="flex-1 min-w-0">
          <h2 className="text-2xl font-bold leading-7 text-gray-900 sm:text-3xl sm:truncate">
            Dashboard
          </h2>
          <p className="mt-1 text-sm text-gray-500">
            Welcome back! Here's what's happening with your sales calls.
          </p>
        </div>
        <div className="mt-4 flex space-x-3 md:mt-0 md:ml-4">
          <button
            onClick={() => setShowAddModal(true)}
            className="inline-flex items-center px-4 py-2 border border-gray-300 text-sm font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500"
          >
            <UserPlusIcon className="h-4 w-4 mr-2" />
            Add Employee
          </button>
          <Link
            to="/upload"
            className="inline-flex items-center px-4 py-2 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-blue-600 hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500"
          >
            <MicrophoneIcon className="h-4 w-4 mr-2" />
            Upload Call
          </Link>
        </div>
      </div>

      {/* Usage Display - Full width */}
      {usageStats && (
        <UsageDisplay
          secondsUsed={usageStats.seconds_used_current_period}
          secondsLimit={usageStats.monthly_minute_limit}
          periodEnd={usageStats.current_period_end}
        />
      )}

      {/* Charts row */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        <CallsPerformanceChart overallAvg={stats?.averageGrade ?? null} />
        <CallsVolumeChart />
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Employee Search */}
        <div className="bg-white shadow rounded-lg">
          <div className="px-4 py-5 sm:px-6 border-b border-gray-200">
            <h3 className="text-lg leading-6 font-medium text-gray-900">
              Find Employee
            </h3>
            <p className="mt-1 max-w-2xl text-sm text-gray-500">
              Search for team members by name
            </p>
          </div>
          <div className="px-4 py-5 sm:p-6">
            <div id="employee-search-container" className="relative">
              <div className="relative">
                <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                  <MagnifyingGlassIcon className="h-5 w-5 text-gray-400" />
                </div>
                <input
                  type="text"
                  className="block w-full pl-10 pr-3 py-2 border border-gray-300 rounded-md leading-5 bg-white placeholder-gray-500 focus:outline-none focus:placeholder-gray-400 focus:ring-1 focus:ring-blue-500 focus:border-blue-500 sm:text-sm"
                  placeholder="Search employees..."
                  value={employeeSearchTerm}
                  onChange={(e) => handleEmployeeSearchChange(e.target.value)}
                />
              </div>

              {/* Search Results Dropdown */}
              {showSearchResults && (
                <div className="absolute z-10 mt-1 w-full bg-white shadow-lg rounded-md border border-gray-200 max-h-60 overflow-auto">
                  {searchLoading ? (
                    <div className="p-4 text-center">
                      <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-blue-600 mx-auto"></div>
                    </div>
                  ) : searchResults.length > 0 ? (
                    searchResults.map((employee) => (
                      <button
                        key={employee.id}
                        onClick={() => selectEmployee(employee)}
                        className="w-full text-left px-4 py-3 hover:bg-gray-50 focus:bg-gray-50 focus:outline-none border-b border-gray-100 last:border-b-0"
                      >
                        <div className="flex items-center">
                          <div className="h-8 w-8 rounded-full bg-blue-100 flex items-center justify-center flex-shrink-0">
                            <span className="text-sm font-medium text-blue-600">
                              {employee.firstName?.charAt(0)}
                              {employee.lastName?.charAt(0)}
                            </span>
                          </div>
                          <div className="ml-3">
                            <p className="text-sm font-medium text-gray-900">
                              {employee.firstName} {employee.lastName}
                            </p>
                            <p className="text-sm text-gray-500">
                              {employee.callCount || 0} calls
                              {employee.avgGrade &&
                                ` • Avg: ${formatGrade(employee.avgGrade)}`}
                            </p>
                          </div>
                        </div>
                      </button>
                    ))
                  ) : (
                    <div className="p-4 text-center text-gray-500 text-sm">
                      No employees found
                    </div>
                  )}
                </div>
              )}
            </div>
          </div>
        </div>

        {/* Recent Calls */}
        <div className="bg-white shadow rounded-lg">
          <div className="px-4 py-5 sm:px-6 border-b border-gray-200">
            <h3 className="text-lg leading-6 font-medium text-gray-900">
              Recent Calls
            </h3>
            <p className="mt-1 max-w-2xl text-sm text-gray-500">
              Latest call analyses from your team
            </p>
          </div>
          <div className="px-4 py-5 sm:p-6">
            {recentCalls.length > 0 ? (
              <ul className="divide-y divide-gray-200">
                {recentCalls.map((call) => (
                  <li key={call.id} className="py-3">
                    <div className="flex items-center justify-between">
                      <div className="flex items-center">
                        <div className="flex-shrink-0">
                          <div className="h-8 w-8 rounded-full bg-gray-100 flex items-center justify-center">
                            <MicrophoneIcon className="h-4 w-4 text-gray-400" />
                          </div>
                        </div>
                        <div className="ml-3">
                          <p className="text-sm font-medium text-gray-900">
                            {call.salesperson_name || "Unknown Salesperson"}
                          </p>
                          <p className="text-xs text-gray-500">
                            {call.audio_file_name}
                          </p>
                        </div>
                      </div>
                      <div className="flex items-center space-x-2">
                        <span
                          className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${getStatusColor(
                            call.status,
                          )}`}
                        >
                          {call.status}
                        </span>
                        {call.average_grade && (
                          <span
                            className={`text-sm font-medium ${getGradeColor(
                              call.average_grade,
                            )}`}
                          >
                            {formatGrade(call.average_grade)}
                          </span>
                        )}
                      </div>
                    </div>
                    <div className="mt-2 flex items-center text-xs text-gray-500">
                      <ClockIcon className="h-4 w-4 mr-1" />
                      {new Date(call.created_at).toLocaleDateString()} at{" "}
                      {new Date(call.created_at).toLocaleTimeString()}
                    </div>
                  </li>
                ))}
              </ul>
            ) : (
              <div className="text-center py-4">
                <p className="text-sm text-gray-500">No recent calls found</p>
                <Link
                  to="/upload"
                  className="mt-2 inline-flex items-center text-sm text-blue-600 hover:text-blue-500"
                >
                  Upload your first call
                </Link>
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Quick Actions */}
      <div className="bg-white shadow rounded-lg">
        <div className="px-4 py-5 sm:px-6 border-b border-gray-200">
          <h3 className="text-lg leading-6 font-medium text-gray-900">
            Quick Actions
          </h3>
        </div>
        <div className="px-4 py-5 sm:p-6">
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
            <Link
              to="/upload"
              className="relative block w-full bg-white p-6 border-2 border-gray-300 border-dashed rounded-lg hover:border-gray-400 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500"
            >
              <div className="text-center">
                <MicrophoneIcon className="mx-auto h-12 w-12 text-gray-400" />
                <span className="mt-2 block text-sm font-medium text-gray-900">
                  Upload New Call
                </span>
              </div>
            </Link>

            <Link
              to="/team"
              className="relative block w-full bg-white p-6 border-2 border-gray-300 border-dashed rounded-lg hover:border-gray-400 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500"
            >
              <div className="text-center">
                <UsersIcon className="mx-auto h-12 w-12 text-gray-400" />
                <span className="mt-2 block text-sm font-medium text-gray-900">
                  View Team Analysis
                </span>
              </div>
            </Link>

            <Link
              to="/employee-management"
              className="relative block w-full bg-white p-6 border-2 border-gray-300 border-dashed rounded-lg hover:border-gray-400 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500"
            >
              <div className="text-center">
                <UserGroupIcon className="mx-auto h-12 w-12 text-gray-400" />
                <span className="mt-2 block text-sm font-medium text-gray-900">
                  View Salespeople
                </span>
              </div>
            </Link>
          </div>
        </div>
      </div>

      {/* Add Employee Modal */}
      <AddSalespersonModal
        isOpen={showAddModal}
        onClose={() => setShowAddModal(false)}
        onSuccess={() => {
          setShowAddModal(false);
          loadDashboardData();
        }}
      />
    </div>
  );
};

export default Dashboard;
